import React, { type FC, type PropsWithChildren } from 'react';

import { getColor } from '@lucid/theme';

import Box from '../box/box';
import Text from '../text/text';
import Heading from '../heading/heading';
import Link from '../link/link';
import type { Lucid } from '../../types/types';
import Container from '../container/container';
import BackNForth from './back-n-forth';

export interface BackNForthSectionProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The section heading */
  heading?: string;
  /** The section text */
  text?: string;
  /** The CTA link and text. */
  link?: Lucid.Directus.Generated.Links;
  /** The content for each section. */
  sections: Lucid.Directus.Generated.Back_N_Forth[];
  /** Determines whether the sections will start in reverse order */
  isReversed?: boolean;
  /** Determines whether the sections will be compact */
  isCompact?: boolean;
  /* The color of the text */
  textColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** The section background color */
  backgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** Optional heading for link text */
  lowerHeading?: string;
}

const BackNForthSection: FC<PropsWithChildren<BackNForthSectionProps>> = ({
  className,
  heading,
  text,
  link,
  sections,
  isCompact = false,
  isReversed = false,
  textColor = 'coolGrey.100',
  backgroundColor,
  lowerHeading,
}) => (
  <Box
    css={theme => ({
      label: 'backNForthSection',
      background: getColor({
        color: backgroundColor,
        theme,
      }),
    })}
    // eslint-disable-next-line @stylistic/jsx-sort-props
    className={className}
  >
    <Container>
      {(heading || text) && (
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            margin: 'auto',
            textAlign: 'center',
            maxWidth: '780px',
          }}
        >
          {heading && (
            <Heading
              as="h2"
              css={theme => [
                {
                  marginTop: theme.space.s80,
                  marginBottom: text ? theme.space.s40 : 0,
                  color: getColor({
                    color: textColor,
                    theme,
                  }),
                },
              ]}
              variant="heading2XL"
            >
              {heading}
            </Heading>
          )}
          {text && <Text variant="paragraphM400">{text}</Text>}
        </Box>
      )}
      {sections && (
        <BackNForth
          hasSectionHeading={!!heading}
          headingVariant="headingXL"
          isCompact={isCompact}
          isReversed={isReversed}
          sections={sections}
          textColor={textColor}
        />
      )}
      {link && (
        <Box
          css={theme => ({
            textAlign: 'center',
            margin: 'auto',
            marginTop: theme.space.s40,
            paddingBottom: theme.space.s40,
          })}
        >
          {lowerHeading && (
            <Text css={theme => ({ color: theme.colors.indigo[60] })} variant="heroXL700">
              {lowerHeading}
            </Text>
          )}
          <Link
            href={link?.final_url}
            iconAnimation="navigational"
            variant={lowerHeading ? 'primaryAlternate' : 'primary'}
          >
            {link?.default_link_text}
          </Link>
        </Box>
      )}
    </Container>
  </Box>
);

export { BackNForthSection };
export default BackNForthSection;
